/**
 * Persona Preview
 *
 * Detailed view of an Engage persona document.
 */

import { motion } from 'framer-motion'

interface Persona {
  id: string
  profile_id: string
  persona_document: {
    summary: string
    communication: {
      preferred_style: string
      formality_level: number
      verbosity: string
      technical_depth: string
    }
    emotional_state?: {
      baseline_tendencies: string[]
      current_indicators: string[]
    }
    interaction_hints?: {
      do: string[]
      avoid: string[]
    }
    behavioral_patterns?: {
      decision_style: string
      information_processing: string
      conflict_approach: string
    }
    confidence: string
  }
  confidence_level: string
  maturity_score?: number
  version: number
  generated_at: string
}

interface PersonaPreviewProps {
  persona: Persona
}

export default function PersonaPreview({ persona }: PersonaPreviewProps) {
  const doc = persona.persona_document

  const getFormalityLabel = (level: number) => {
    if (level >= 0.7) return 'Formal'
    if (level >= 0.4) return 'Moderate'
    return 'Casual'
  }

  const getConfidenceColor = (level: string) => {
    switch (level) {
      case 'mature': return 'bg-green-100 text-green-800'
      case 'established': return 'bg-blue-100 text-blue-800'
      case 'emerging': return 'bg-yellow-100 text-yellow-800'
      case 'minimal': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="divide-y divide-gray-100">
      {/* Header */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className={`text-xs px-2 py-1 rounded-full ${getConfidenceColor(persona.confidence_level)}`}>
            {persona.confidence_level}
          </span>
          <span className="text-xs text-gray-400">v{persona.version}</span>
        </div>
        <p className="text-sm text-gray-700 leading-relaxed">
          {doc.summary}
        </p>
        <p className="text-xs text-gray-400 mt-2">
          Generated {new Date(persona.generated_at).toLocaleDateString()}
        </p>
      </div>

      {/* Communication Style */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="p-4"
      >
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Communication Style
        </h4>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Style</span>
            <span className="text-sm font-medium text-gray-900">
              {doc.communication.preferred_style}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Formality</span>
            <div className="flex items-center gap-2">
              <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-engage-primary rounded-full"
                  style={{ width: `${doc.communication.formality_level * 100}%` }}
                />
              </div>
              <span className="text-xs text-gray-500">
                {getFormalityLabel(doc.communication.formality_level)}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Verbosity</span>
            <span className="text-sm font-medium text-gray-900 capitalize">
              {doc.communication.verbosity}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Technical Depth</span>
            <span className="text-sm font-medium text-gray-900 capitalize">
              {doc.communication.technical_depth}
            </span>
          </div>
        </div>
      </motion.div>

      {/* Interaction Hints */}
      {doc.interaction_hints && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="p-4"
        >
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Interaction Hints
          </h4>
          <div className="space-y-3">
            {doc.interaction_hints.do && doc.interaction_hints.do.length > 0 && (
              <div>
                <p className="text-xs font-medium text-green-600 mb-1.5">Do:</p>
                <ul className="space-y-1">
                  {doc.interaction_hints.do.slice(0, 3).map((hint, i) => (
                    <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      {hint}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {doc.interaction_hints.avoid && doc.interaction_hints.avoid.length > 0 && (
              <div>
                <p className="text-xs font-medium text-red-600 mb-1.5">Avoid:</p>
                <ul className="space-y-1">
                  {doc.interaction_hints.avoid.slice(0, 3).map((hint, i) => (
                    <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">✗</span>
                      {hint}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Behavioral Patterns */}
      {doc.behavioral_patterns && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="p-4"
        >
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Behavioral Patterns
          </h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Decision Style</span>
              <span className="text-sm font-medium text-gray-900 capitalize">
                {doc.behavioral_patterns.decision_style}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Info Processing</span>
              <span className="text-sm font-medium text-gray-900 capitalize">
                {doc.behavioral_patterns.information_processing}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Conflict Approach</span>
              <span className="text-sm font-medium text-gray-900 capitalize">
                {doc.behavioral_patterns.conflict_approach}
              </span>
            </div>
          </div>
        </motion.div>
      )}

      {/* Emotional State */}
      {doc.emotional_state?.baseline_tendencies && doc.emotional_state.baseline_tendencies.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="p-4"
        >
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Emotional Baseline
          </h4>
          <div className="flex flex-wrap gap-2">
            {doc.emotional_state.baseline_tendencies.map((tendency, i) => (
              <span
                key={i}
                className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full"
              >
                {tendency}
              </span>
            ))}
          </div>
        </motion.div>
      )}

      {/* Actions */}
      <div className="p-4 bg-gray-50">
        <div className="flex gap-2">
          <button className="flex-1 px-3 py-2 text-sm font-medium text-engage-primary hover:bg-engage-primary/10 rounded-lg transition-colors">
            Export
          </button>
          <button className="flex-1 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded-lg transition-colors">
            Refresh
          </button>
        </div>
      </div>
    </div>
  )
}
